/**
 * An overlay menu drawn from core's shared menu structure (`SETTINGS_MENU`,
 * `ROOM_EDITOR_VIEW_MENU`, `FLOOR_PLAN_VIEW_MENU`): the button's icon, and
 * the card's headings, checkboxes and radio groups, read from and written to
 * the editors' preferences. Kai Pro draws the same rows with its own widgets.
 */

import {
  type FloorPlanMenu,
  type FloorPlanMenuRow,
  type FloorPlanPreferences,
  ICON_SIZE,
  ICON_VIEW_BOX,
  type IconPaths,
  iconStrokeWidth,
  menuCheckboxChecked,
  menuCheckboxPatch,
  menuRadioPatch,
  updateFloorPlanPreferences,
  useFloorPlanPreferences,
} from '@kaiizen/kai-floor-plan-editor-core';
import { MENU_CHOICE_STYLE, MenuHead, OverlayMenu } from './OverlayMenu';

/** A shared icon, stroked in the button's own color. */
export function MenuIcon({ paths }: { paths: IconPaths }): React.JSX.Element {
  return (
    <svg viewBox={ICON_VIEW_BOX} aria-hidden='true'>
      {paths.map(path => (
        <path key={path} d={path} fill='none' stroke='currentColor' strokeWidth={iconStrokeWidth(ICON_SIZE)} strokeLinecap='round' strokeLinejoin='round' />
      ))}
    </svg>
  );
}

function MenuRowView({ row, preferences }: { row: FloorPlanMenuRow; preferences: FloorPlanPreferences }): React.JSX.Element {
  switch (row.kind) {
    case 'heading':
      return <MenuHead>{row.label}</MenuHead>;
    case 'checkbox':
      return (
        <label css={MENU_CHOICE_STYLE}>
          <input
            type='checkbox'
            name={row.preference === null ? undefined : `floor-plan-${row.preference === 'layers' ? row.layer : row.preference}`}
            checked={menuCheckboxChecked(row, preferences)}
            disabled={row.preference === null}
            onChange={event => updateFloorPlanPreferences(menuCheckboxPatch(row, preferences, event.target.checked))}
          />
          <span>{row.label}</span>
        </label>
      );
    case 'radio':
      return (
        <>
          <MenuHead>{row.label}</MenuHead>
          {row.choices.map(choice => (
            <label key={choice.id} css={MENU_CHOICE_STYLE}>
              <input
                type='radio'
                name={`floor-plan-${row.preference}`}
                value={choice.id}
                checked={preferences[row.preference] === choice.id}
                onChange={() => updateFloorPlanPreferences(menuRadioPatch(row, choice.id))}
              />
              {choice.label}
              {choice.example && <small>{choice.example}</small>}
            </label>
          ))}
        </>
      );
  }
}

export interface FloorPlanMenuCardProps {
  menu: FloorPlanMenu;
  /** The button's test id; the card's is the same with `-card` in place of `-button`. */
  testId: string;
}

export function FloorPlanMenuCard({ menu, testId }: FloorPlanMenuCardProps): React.JSX.Element {
  const preferences = useFloorPlanPreferences();
  return (
    <OverlayMenu label={menu.label} icon={<MenuIcon paths={menu.icon} />} testId={testId}>
      {menu.rows.map(row => <MenuRowView key={`${row.kind}-${row.label}`} row={row} preferences={preferences} />)}
    </OverlayMenu>
  );
}
